import { Link, router } from '@inertiajs/react'
import { Plus } from 'lucide-react'
import { BackofficeLayout } from '@/components/layout/BackofficeLayout'
import { SearchInput } from '@/components/shared/SearchInput'
import { Pagination, PaginationMeta } from '@/components/shared/Pagination'
import { Button } from '@/components/ui/button'
import { CountrySummary } from '@/types'

type Props = { countries: CountrySummary[]; search: string; pagination: PaginationMeta }

export default function CountriesIndex({ countries, search, pagination }: Props) {
  return (
    <BackofficeLayout title="Jurisdictions" breadcrumbs={[{ label: 'Backoffice', href: '/backoffice/dashboard' }, { label: 'Jurisdictions' }]}>
      <div className="mb-8">
        <h1 className="text-xl font-semibold tracking-tight">Jurisdictions</h1>
        <p className="text-sm text-muted-foreground">{pagination.total} {pagination.total === 1 ? 'country' : 'countries'}</p>
        <div className="flex items-center gap-2 mt-3">
          <Button size="sm" variant="outline" asChild className="gap-1.5 text-[13px] h-8">
            <Link href="/backoffice/countries/new">
              <Plus className="w-3.5 h-3.5" />
              New Country
            </Link>
          </Button>
        </div>
      </div>

      <SearchInput value={search} placeholder="Search countries..." />

      <div className="mt-4">
        {countries.length === 0 ? (
          <div className="border rounded-lg py-16 text-center">
            <h3 className="text-sm font-medium text-foreground mb-1">No countries configured yet</h3>
          </div>
        ) : (
          <div className="border rounded-lg">
            {countries.map((c, i) => (
              <div
                key={c.id}
                className={`px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors ${i > 0 ? 'border-t' : ''}`}
                onClick={() => router.visit(`/backoffice/countries/${c.id}`)}
              >
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <Link href={`/backoffice/countries/${c.id}`} className="text-sm font-medium hover:underline">{c.name}</Link>
                    <span className="text-sm font-mono text-muted-foreground ml-2">{c.code}</span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground ml-4">
                    <span>{c.states_count} states</span>
                    <span>{c.entities_count} entities</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-4">
        <Pagination pagination={pagination} />
      </div>
    </BackofficeLayout>
  )
}
